import {
  ArrowRightIcon,
  CalendarDaysIcon,
  FilterXIcon,
  MapPinIcon,
  PlusIcon,
  RefreshCwIcon,
  SearchIcon,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { TradeFairSort, TradeFairState, type TradeFair } from '@/api/tradeFairs'
import { useApiErrorMessage } from '@/api/errors'
import { Role } from '@/auth/roles'
import { useAuth } from '@/auth/useAuth'
import { ClearFiltersButton } from '@/components/ClearFiltersButton'
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardFooter } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TooltipProvider } from '@/components/ui/tooltip'
import { ActivatableRow } from '@/components/layout/ActivatableRow'
import { PageHeader } from '@/components/layout/PageHeader'
import { Pagination } from '@/components/layout/Pagination'
import { SortableHead } from '@/components/layout/SortableHead'
import {
  EDGE_PADDED_TABLE,
  FULL_HEIGHT_CARD,
  PHONE_SUMMARY,
  PHONE_WRAPPING_CELL,
  SCROLLING_TABLE,
  STICKY_TABLE_HEAD,
  WIDE_ONLY,
} from '@/components/layout/tableStyles'
import { TradeFairDialog } from '@/components/tradefairs/TradeFairDialog'
import { TradeFairRowActions } from '@/components/tradefairs/TradeFairRowActions'
import { TradeFairStateBadge } from '@/components/tradefairs/TradeFairStateBadge'
import { FILTERABLE_STATES, STATE_LABEL_KEYS } from '@/components/tradefairs/stateLabels'
import { useTradeFairList } from '@/hooks/useTradeFairList'
import { formatPeriod } from '@/lib/dates'
import { cn } from '@/lib/utils'

const COLUMN_COUNT = 6

/** The Select needs a value for "no filter", and an empty string is not one Radix accepts. */
const ALL_STATES = 'ALL'

export function TradeFairsPage() {
  const { t } = useTranslation()
  const { hasRole } = useAuth()
  const errorMessage = useApiErrorMessage()
  const list = useTradeFairList()

  // Hidden rather than disabled, as in the sidebar: a control that always 403s is worse than no
  // control. The backend enforces the same rule regardless of what is rendered.
  const navigate = useNavigate()
  const canEdit = hasRole(Role.ADMIN)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<TradeFair | null>(null)

  const fairs = list.page?.tradeFairs ?? []
  const total = list.page?.total ?? 0

  function openCreate() {
    setEditing(null)
    setDialogOpen(true)
  }

  function openEdit(fair: TradeFair) {
    setEditing(fair)
    setDialogOpen(true)
  }

  return (
    <TooltipProvider>
      <PageHeader title={t('tradeFairs.title')}>
        {canEdit && (
          <Button onClick={openCreate}>
            <PlusIcon />
            {t('tradeFairs.actions.create')}
          </Button>
        )}
      </PageHeader>

      <Card className={FULL_HEIGHT_CARD}>
        <div className="flex flex-wrap items-center gap-3 border-b p-4 shrink-0">
          <div className="relative min-w-56 flex-1">
            <SearchIcon
              className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
              aria-hidden
            />
            <Input
              type="search"
              className="pl-8"
              aria-label={t('tradeFairs.searchLabel')}
              placeholder={t('tradeFairs.searchPlaceholder')}
              value={list.search}
              onChange={(event) => list.setSearch(event.target.value)}
            />
          </div>

          <Select
            value={list.state ?? ALL_STATES}
            onValueChange={(next) =>
              list.setState(next === ALL_STATES ? null : (next as TradeFairState))
            }
          >
            <SelectTrigger className="w-44" aria-label={t('tradeFairs.filterLabel')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_STATES}>{t('tradeFairs.filterAll')}</SelectItem>
              {FILTERABLE_STATES.map((state) => (
                <SelectItem key={state} value={state}>
                  {t(STATE_LABEL_KEYS[state])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <ClearFiltersButton
            active={list.filtered}
            label={t('common.clearFilters')}
            onClick={list.clearFilters}
          />

          <Button
            variant="outline"
            size="icon"
            aria-label={t('tradeFairs.reload')}
            onClick={list.reload}
          >
            <RefreshCwIcon />
          </Button>
        </div>

        {list.error ? (
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            <Alert variant="destructive">
              <AlertTitle>{t('tradeFairs.loadFailed')}</AlertTitle>
              <AlertDescription>
                {errorMessage(list.error, t('common.unexpectedError'))}
              </AlertDescription>
              <AlertAction>
                <Button variant="outline" size="sm" onClick={list.reload}>
                  {t('tradeFairs.retry')}
                </Button>
              </AlertAction>
            </Alert>
          </div>
        ) : (
          <Table
            className={EDGE_PADDED_TABLE}
            containerClassName={SCROLLING_TABLE}
            // The rows on screen are the previous page while the next one loads. This is what
            // says so to a screen reader; the dimming below is what says it to everybody else.
            aria-busy={list.loading}
          >
            <TableHeader className={STICKY_TABLE_HEAD}>
              <TableRow>
                <SortableHead
                  column={TradeFairSort.NAME}
                  sort={list.sort}
                  direction={list.direction}
                  onToggle={list.toggleSort}
                >
                  {t('tradeFairs.columns.name')}
                </SortableHead>
                <SortableHead
                  column={TradeFairSort.START_DATE}
                  sort={list.sort}
                  direction={list.direction}
                  onToggle={list.toggleSort}
                  className={WIDE_ONLY}
                >
                  {t('tradeFairs.columns.period')}
                </SortableHead>
                <SortableHead
                  column={TradeFairSort.CITY}
                  sort={list.sort}
                  direction={list.direction}
                  onToggle={list.toggleSort}
                  className={WIDE_ONLY}
                >
                  {t('tradeFairs.columns.location')}
                </SortableHead>
                {/* Not sortable: the state is worked out from the period against today and never
                    stored, so there is no column to order by — the period is that order. */}
                <TableHead className={WIDE_ONLY}>{t('tradeFairs.columns.state')}</TableHead>
                <TableHead className={WIDE_ONLY}>{t('tradeFairs.columns.leads')}</TableHead>
                <TableHead className="w-0 text-right">
                  <span className="sr-only">{t('tradeFairs.columns.actions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody
              // Held back while the next page is on its way, rather than blanked: the rows are
              // still true, they are just no longer the answer to what was asked.
              className={list.loading && fairs.length > 0 ? 'opacity-60 transition-opacity' : undefined}
            >
              {/* Only while there is nothing to show: repainting a populated table as skeletons on
                  every keystroke is worse than leaving the old rows up. */}
              {list.loading && fairs.length === 0 && <LoadingRows />}

              {!list.loading && fairs.length === 0 && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={COLUMN_COUNT} className="h-40">
                    <div className="text-muted-foreground flex flex-col items-center justify-center gap-2 text-center">
                      <CalendarDaysIcon className="size-6" aria-hidden />
                      <p className="text-sm">
                        {list.search
                          ? t('tradeFairs.emptySearch', { search: list.search })
                          : list.state
                            ? t('tradeFairs.emptyFilter')
                            : t('tradeFairs.empty')}
                      </p>
                      {/* The way out of the state somebody is looking at: back to the whole list
                          when a filter emptied it, and the first fair when there are none. */}
                      {list.filtered ? (
                        <Button variant="outline" size="sm" onClick={list.clearFilters}>
                          <FilterXIcon />
                          {t('common.clearFilters')}
                        </Button>
                      ) : (
                        canEdit && (
                          <Button variant="outline" size="sm" onClick={openCreate}>
                            <PlusIcon />
                            {t('tradeFairs.actions.create')}
                          </Button>
                        )
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {fairs.map((fair) => (
                <ActivatableRow
                  key={fair.id}
                  data-testid={`trade-fair-${fair.id}`}
                  // The row opens the fair itself, like every other list; its leads are the link
                  // in their own column. Somebody who cannot edit has no fair to open, so for
                  // them the row keeps leading to the leads rather than doing nothing.
                  onActivate={() => (canEdit ? openEdit(fair) : navigate(leadsOf(fair)))}
                >
                  <TableCell className={PHONE_WRAPPING_CELL}>
                    <div className="flex flex-col">
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium">
                        {fair.name}
                        <span className="md:hidden">
                          <TradeFairStateBadge state={fair.state} />
                        </span>
                      </span>
                      {fair.notes && (
                        <span className="text-muted-foreground line-clamp-1 text-xs">
                          {fair.notes}
                        </span>
                      )}
                      <span className={PHONE_SUMMARY}>
                        {formatPeriod(fair.startDate, fair.endDate)} · {fair.city}
                        {/* The way to the fair's leads, which have no column on a phone. */}
                        {fair.leadCount > 0 && (
                          <>
                            {' · '}
                            <Link
                              to={leadsOf(fair)}
                              className="text-primary font-medium"
                              onClick={(event) => event.stopPropagation()}
                            >
                              {t('tradeFairs.leadCount', { count: fair.leadCount })}
                            </Link>
                          </>
                        )}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className={cn('whitespace-nowrap', WIDE_ONLY)}>
                    {formatPeriod(fair.startDate, fair.endDate)}
                  </TableCell>
                  <TableCell className={WIDE_ONLY}>
                    <div className="flex flex-col">
                      <span className="flex items-center gap-1.5">
                        <MapPinIcon className="text-muted-foreground size-3.5" aria-hidden />
                        {fair.city}
                      </span>
                      {/* Our own stand, which is what somebody looking this up wants to know. */}
                      {(fair.hall || fair.booth) && (
                        <span className="text-muted-foreground pl-5 text-xs">
                          {[fair.hall, fair.booth].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className={WIDE_ONLY}>
                    <TradeFairStateBadge state={fair.state} />
                  </TableCell>
                  <TableCell className={cn('whitespace-nowrap', WIDE_ONLY)}>
                    {fair.leadCount > 0 ? (
                      // A real link rather than a button: it opens in a new tab, and the lead
                      // list keeps its filters in the address bar, so the back button leads here
                      // again. Labelled with the count, so it says where it goes without a tooltip.
                      <Link
                        to={leadsOf(fair)}
                        aria-label={t('tradeFairs.actions.showLeads', {
                          count: fair.leadCount,
                          name: fair.name,
                        })}
                        className="text-primary focus-visible:outline-ring inline-flex items-center gap-1 rounded-sm text-sm font-medium underline-offset-4 hover:underline focus-visible:outline-2"
                        // The row underneath opens the fair; the link must not do both.
                        onClick={(event) => event.stopPropagation()}
                      >
                        {t('tradeFairs.leadCount', { count: fair.leadCount })}
                        <ArrowRightIcon className="size-3.5" aria-hidden />
                      </Link>
                    ) : (
                      // Not a link: it would lead to an empty list.
                      <span className="text-muted-foreground text-sm">{t('tradeFairs.noLeads')}</span>
                    )}
                  </TableCell>
                  <TableCell
                    className="text-right"
                    // The row opens the fair; the menu inside it must not do both.
                    onClick={(event) => event.stopPropagation()}
                  >
                    {canEdit && (
                      <TradeFairRowActions
                        fair={fair}
                        onEdit={() => openEdit(fair)}
                        onChanged={list.reload}
                      />
                    )}
                  </TableCell>
                </ActivatableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {!list.error && total > 0 && (
          <CardFooter className="block shrink-0">
            <Pagination
              pageIndex={list.pageIndex}
              pageCount={list.pageCount}
              pageSize={list.pageSize}
              range={list.range}
              onPageChange={list.setPageIndex}
              onPageSizeChange={list.setPageSize}
            />
          </CardFooter>
        )}
      </Card>

      <TradeFairDialog
        open={dialogOpen}
        fair={editing}
        onOpenChange={setDialogOpen}
        onSaved={list.reload}
      />
    </TooltipProvider>
  )
}

function leadsOf(fair: TradeFair): string {
  return `/leads?fair=${encodeURIComponent(fair.id)}`
}

function LoadingRows() {
  return (
    <>
      {Array.from({ length: 5 }, (_, index) => (
        <TableRow key={index} className="hover:bg-transparent">
          <TableCell colSpan={COLUMN_COUNT} className="py-3">
            <Skeleton className="h-8 w-full" />
          </TableCell>
        </TableRow>
      ))}
    </>
  )
}
